import { useCallback } from 'react';
import reduce from 'lodash/reduce';

const useGet = (api, data, cb, cbError) => {
    return useCallback(
        (params, options, updateState = true) => {
            return api
                .get(params, options)
                .then((res) => {
                    if (updateState) cb(res);
                    return res;
                })
                .catch((err) => {
                    cbError(err);
                    throw err;
                });
        },
        [api, cb, cbError]
    );
};

const useGetById = (api, data, cb, cbError) => {
    return useCallback(
        (id, updateState = true) => {
            return api
                .getById(id)
                .then((res) => {
                    if (updateState) {
                        cb(res);
                    }
                    return res;
                })
                .catch(cbError);
        },
        [api, cb, cbError]
    );
};

const useGetOne = (api, cb, cbError) => {
    return useCallback(
        (params, body, updateState = true) => {
            return api
                .getOne(params, body)
                .then((res) => {
                    if (updateState) {
                        cb(res);
                    }
                    return res;
                })
                .catch((err) => {
                    cbError(err);
                    throw err;
                });
        },
        [api, cb, cbError]
    );
};

const useGetSchema = (api, data, cb, cbError) => {
    return useCallback(
        (query, updateState = true) => {
            return api
                .getSchema(query)
                .then((res) => {
                    if (updateState) {
                        cb(res);
                    }
                    return res;
                })
                .catch(cbError);
        },
        [api, cb, cbError]
    );
};

const useRemove = (api, data, cb, cbError) => {
    return useCallback(
        (id, updateState = true) =>
            api
                .remove(id)
                .then((deletedID) => {
                    if (updateState) {
                        let newData;
                        if (Array.isArray(data)) {
                            newData = data.filter((item) => item._id !== deletedID);
                        } else {
                            newData = reduce(
                                data.byId,
                                (acc, next) => {
                                    if (next._id === deletedID) {
                                        return acc;
                                    }
                                    acc.byId[next._id] = next;
                                    acc.ids = acc.ids.concat(next._id);
                                    return acc;
                                },
                                { ids: [], byId: {} }
                            );
                        }
                        cb(deletedID, newData);
                    }
                    return deletedID;
                })
                .catch((err) => {
                    cbError(err);
                    throw err;
                }),
        [data, api, cb, cbError]
    );
};

const usePost = (api, data, cb, cbError) => {
    return useCallback(
        (body, updateState = true, last = true) => {
            return api
                .post(body)
                .then((item) => {
                    if (Array.isArray(item.draft) && item.draft.length && !item.deleted) {
                        return item;
                    }
                    if (updateState) {
                        let newData;
                        if (Array.isArray(data)) {
                            if (last) {
                                newData = data.concat(item);
                            } else {
                                newData = [item, ...data];
                            }
                        } else {
                            newData = reduce(
                                data.ids.concat(item._id),
                                (acc, next) => {
                                    if (!data.byId[next]) {
                                        acc.byId[next] = item;
                                    } else {
                                        acc.byId[next] = data.byId[next];
                                    }
                                    acc.ids[last ? 'push' : 'unshift'](next);
                                    return acc;
                                },
                                { ids: [], byId: {} }
                            );
                        }
                        cb(item, newData);
                    }

                    return item;
                })
                .catch((err) => {
                    cbError(err);
                    throw err;
                });
        },
        [data, api, cb, cbError]
    );
};

const usePut = (api, data, cb, cbError) => {
    return useCallback(
        (body, updateState = true) => {
            return api
                .put(body)
                .then((response) => {
                    if (updateState) {
                        let newData;
                        if (Array.isArray(data)) {
                            newData = data.map((v) => {
                                return v._id === response._id ? response : v;
                            });
                        } else {
                            newData = data.ids.reduce(
                                (acc, next) => {
                                    if (next === response._id) {
                                        if (response.deleted) {
                                            return acc;
                                        }
                                        acc.byId[next] = response;
                                    } else {
                                        acc.byId[next] = data.byId[next];
                                    }
                                    acc.ids = acc.ids.concat(next);
                                    return acc;
                                },
                                { ids: [], byId: {} }
                            );
                        }
                        cb(response, newData);
                    }
                    return response;
                })
                .catch((err) => {
                    cbError(err);
                    throw err;
                });
        },
        [data, api, cb, cbError]
    );
};

const useSoftDelete = (api, data, cb, cbError) => {
    return useCallback(
        (id, updateState = true) =>
            api
                .softDelete(id)
                .then((deletedInstance) => {
                    const { _id: deletedID } = deletedInstance;
                    if (updateState) {
                        let newData;
                        if (Array.isArray(data)) {
                            newData = data.filter((item) => item._id !== deletedID);
                        } else {
                            newData = reduce(
                                data.byId,
                                (acc, next) => {
                                    if (next._id === deletedID) {
                                        return acc;
                                    }
                                    acc.byId[next._id] = next;
                                    acc.ids = acc.ids.concat(next._id);
                                    return acc;
                                },
                                { ids: [], byId: {} }
                            );
                        }
                        cb(deletedID, newData);
                    }
                    return deletedID;
                })
                .catch((err) => {
                    cbError(err);
                    throw err;
                }),
        [data, api, cb, cbError]
    );
};

export {
    usePost,
    useGet,
    useGetOne,
    useRemove,
    useGetById,
    useGetSchema,
    usePut,
    useSoftDelete
};
